import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Pencil, Save, X, ChevronDown, RotateCcw } from "lucide-react";
import { getGuide, saveGuide } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") ? <strong key={i} className="text-white">{p.slice(2, -2)}</strong> : p));

const Rendered = ({ text }) => {
  const lines = text.split("\n");
  const out = [];
  let faq = null;
  const flush = () => { if (faq) { out.push(faq); faq = null; } };
  lines.forEach((line, i) => {
    if (line.startsWith("### ")) {
      flush();
      faq = { q: line.slice(4), a: [], key: i };
      return;
    }
    if (faq && !line.startsWith("# ") && !line.startsWith("## ")) { faq.a.push(line); return; }
    flush();
    if (line.startsWith("# ")) out.push(<h2 key={i} className="font-serif text-3xl text-white pt-2">{line.slice(2)}</h2>);
    else if (line.startsWith("## ")) out.push(<h3 key={i} className="font-serif text-2xl text-[#93C5FD] pt-8 border-t border-white/10 mt-6">{line.slice(3)}</h3>);
    else if (line.startsWith("- ")) out.push(<p key={i} className="pl-5 relative before:content-['•'] before:absolute before:left-1 before:text-[#93C5FD]">{inline(line.slice(2))}</p>);
    else if (line.trim()) out.push(<p key={i}>{inline(line)}</p>);
  });
  flush();
  return (
    <div className="space-y-2 text-[#F5F5F0]/80 leading-relaxed" data-testid="guide-rendered">
      {out.map((item) => item.q === undefined ? item : (
        <details key={item.key} className="group rounded-xl border border-white/10 bg-white/[0.03] open:bg-white/[0.06]" data-testid="guide-faq-item">
          <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-3 text-white font-medium"><span>{item.q}</span><ChevronDown className="w-4 h-4 text-[#93C5FD] transition-transform group-open:rotate-180" /></summary>
          <div className="px-4 pb-4 space-y-1.5">{item.a.filter((l) => l.trim()).map((l, j) => l.startsWith("- ") ? <p key={j} className="pl-5 relative before:content-['•'] before:absolute before:left-1 before:text-[#93C5FD]">{inline(l.slice(2))}</p> : <p key={j}>{inline(l)}</p>)}</div>
        </details>
      ))}
    </div>
  );
};

export const StaffGuide = () => {
  const [text, setText] = useState("");
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  useEffect(() => { getGuide().then((g) => setText(g.text)).catch(() => toast.error("Couldn't load the guide")); }, []);
  const save = async () => {
    try { await saveGuide(draft); setText(draft); setEditing(false); toast.success("Guide saved"); }
    catch { toast.error("Could not save"); }
  };
  return (
    <div className="border border-white/10 rounded-xl p-6 md:p-8 max-w-3xl" data-testid="staff-guide">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#93C5FD]" /><p className="overline text-[#F5F5F0]/70">Staff guide</p></div>
        {editing ? (
          <div className="flex gap-2">
            <Button data-testid="guide-reset-btn" variant="outline" onClick={async () => { try { await saveGuide("RESET"); const g = await getGuide(); setText(g.text); setEditing(false); toast.success("Guide reset to the default"); } catch { toast.error("Could not reset"); } }} className="rounded-full border-white/20 bg-transparent text-white/70 hover:bg-white/10"><RotateCcw className="w-4 h-4 mr-1" /> Default</Button>
            <Button data-testid="guide-cancel-btn" variant="outline" onClick={() => setEditing(false)} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button data-testid="guide-save-btn" onClick={save} className="rounded-full bg-[#F5F5F0] text-[#001A4D] hover:bg-white"><Save className="w-4 h-4 mr-1" /> Save</Button>
          </div>
        ) : (
          <Button data-testid="guide-edit-btn" variant="outline" onClick={() => { setDraft(text); setEditing(true); }} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
        )}
      </div>
      {editing ? (
        <>
          <Textarea data-testid="guide-editor" value={draft} onChange={(e) => setDraft(e.target.value)} rows={28} className="bg-white/5 border-white/15 text-white font-mono text-sm" />
          <p className="text-xs text-white/40 mt-2">Formatting: <code># Heading</code>, <code>## Section</code>, <code>### Question</code> (becomes a tap-to-open answer), <code>- bullet</code>, <code>**bold**</code>.</p>
        </>
      ) : <Rendered text={text} />}
    </div>
  );
};
